/**
 * Разовое открытие ОДНОГО расчёта за 199 ₽ — «Открыть полностью — 199 ₽».
 *
 * Механика та же, что у пары в Совместимости: перед оплатой расчёт
 * сохраняется на сервере (POST /api/calculator/unlocks → id), платёж несёт
 * только id, после оплаты кнопка экрана успеха ведёт на страницу программы
 * с ?unlock=<id>, и страница пересчитывает тот же расчёт — сервер уже
 * отдаёт его полностью. Повторно тот же расчёт открывается без оплаты.
 * Сервер: backend/calc_unlock.py.
 *
 * payload — ровно тело запроса расчёта программы (у «Событий на день» —
 * { date }, у натальной карты — { planets }).
 */
import { BACKEND_URL as API_URL } from '@/lib/backendUrl';
import { createPrimaryPaymentNow, createOneTimePaymentNow } from '@/lib/paymentProvider';
import { singleCheckout } from '@/lib/checkoutGuard';
import { ymGoal, ymClientIdAsync } from '@/lib/analytics';
import { SITE_HOST, siteText } from '@/lib/siteConfig';
import { rubIsRobokassa } from '@/lib/rubProvider';
import { STRIPE_PRIMARY, foreignOneTimePrice } from '@/lib/visitorRegion';

export const CALC_UNLOCK_PRICE = 199;
export const CALC_UNLOCK_RETURN_KEY = 'calc_unlock_return';
// Страница, откуда купили, если она не cfg.path (скрытая /money-new до
// переключения /money): экран успеха ведёт обратно на неё.
export const CALC_UNLOCK_RETURN_PATH_KEY = 'calc_unlock_return_path';
export const CALC_UNLOCK_PARAM = 'unlock';

// app → код плана (он же имя программы в параметре plan цели Метрики), куда
// вернуться после оплаты и строка экрана успеха. Совпадает с backend
// calc_unlock.APPS.
export const CALC_UNLOCK_APPS = {
  natal: { plan: 'NATAL_ONE_TIME', name: 'Натальная карта', path: '/', opened: 'Натальная карта открыта полностью', cta: 'Открыть мою карту' },
  money: { plan: 'MONEY_ONE_TIME', name: 'Где твои деньги', path: '/money', cta: 'Открыть расчёт' },
  daily3: { plan: 'DAILY3_ONE_TIME', name: 'События на день', path: '/transits/results', opened: 'День открыт полностью', cta: 'Открыть мой день' },
  women: { plan: 'WOMEN_ONE_TIME', name: 'Женские события', path: '/women', opened: 'День открыт полностью', cta: 'Открыть мой день' },
  events_month: { plan: 'EVENTS_MONTH_ONE_TIME', name: 'Твой месяц', path: '/events-month', opened: 'Месяц открыт полностью', cta: 'Открыть мой месяц' },
  work: { plan: 'WORK_ONE_TIME', name: 'Профессия', path: '/work', cta: 'Открыть расчёт' },
  mum: { plan: 'MUM_ONE_TIME', name: 'Партнёр', path: '/mum', cta: 'Открыть расчёт' },
  uzel: { plan: 'UZEL_ONE_TIME', name: 'Предназначение', path: '/uzel', cta: 'Открыть расчёт' },
  love: { plan: 'LOVE_PERIODS_ONE_TIME', name: 'Любовь и замужество', path: '/love-periods', opened: 'Год открыт полностью', cta: 'Открыть мой год' },
  money_periods: { plan: 'MONEY_PERIODS_ONE_TIME', name: 'Когда придут деньги', path: '/money-periods', opened: 'Год открыт полностью', cta: 'Открыть мой год' },
  obraz: { plan: 'OBRAZ_ONE_TIME', name: 'Образ — как тебя видят', path: '/obraz', opened: 'Образ открыт полностью', cta: 'Открыть мой образ' },
  // Чакры пары (вкладка АстроЧакр): одна пара, payload — { a, b }.
  chakry_para: { plan: 'CHAKRY_PARA_ONE_TIME', name: 'Чакры пары', path: '/chakry-pary', opened: 'Чакры пары открыты полностью', cta: 'Открыть чакры пары' },
  // «Нумерология» (num_v2): на /numerology с 12.10.2026.
  num: { plan: 'NUM_ONE_TIME', name: 'Нумерология', path: '/numerology', opened: 'Нумерология открыта полностью', cta: 'Открыть мои числа' },
  // «Как его влюбить»: 199 ₽ открывают ключи 2–4; ключи 5, 6 и Секретный — только ПРО.
  vlyubit: { plan: 'VLYUBIT_ONE_TIME', name: 'Как его влюбить', path: '/vlyubit', opened: 'Ключи 2–4 открыты', cta: 'Открыть его ключи' },
  // «Твой год» (/year): весь год общего колеса — как колёса Любви и Денег.
  year: { plan: 'YEAR_ONE_TIME', name: 'Твой год', path: '/year', opened: 'Год открыт полностью', cta: 'Открыть мой год' },
};

// Через Робокассу разовых планов нет: /robokassa/create отвечает
// «Неизвестный план». Поэтому при кассе Робокасса кнопку 199 ₽ не показываем
// нигде, где покупка падает: все программы разового открытия расчёта
// (CALC_UNLOCK_APPS) и АстроЧакры. Ссылка на ПРО остаётся.
// С 30.10.2026 касса — та, что назвал бэкенд (lib/rubProvider.js
// rubIsRobokassa), а не флаг сборки ру: рубли ру идут через ЮKassa, и
// россиянин на ру видит разовые покупки, как на рф (решение владелицы).
// Иностранцу на ру (STRIPE_PRIMARY, lib/visitorRegion.js) разовая покупка
// снова показывается — её продаёт Stripe по цене 1 190 ₸ / 2,49 $.
// 'para' — пара в Совместимости (PARA_PAIR_ONE_TIME): Робокасса этот план
// тоже не знает: при Робокассе кнопки нет.
export const ONE_TIME_HIDDEN_ON_ROBOKASSA = [...Object.keys(CALC_UNLOCK_APPS), 'chakry', 'para'];

export function oneTimeSaleShown(app, isRobokassa = rubIsRobokassa(), stripePrimary = STRIPE_PRIMARY) {
  return !(isRobokassa && !stripePrimary && ONE_TIME_HIDDEN_ON_ROBOKASSA.includes(app));
}

/**
 * Продаётся ли посетителю разовое открытие программы вообще: на рф — да,
 * иностранцу на ру — да (Stripe), при кассе Робокасса — нет (разовых
 * планов не знает, кнопок нет), и тексты обещают ему только ПРО.
 */
export function oneTimeOffered(isRobokassa = rubIsRobokassa(), stripePrimary = STRIPE_PRIMARY) {
  return !(isRobokassa && !stripePrimary);
}

/** Цена разовой покупки на кнопках: «199 ₽», иностранцу на ру — «2,49 $» / «1 190 ₸». */
export function oneTimePriceLabel(stripePrimary = STRIPE_PRIMARY, tz = undefined) {
  return stripePrimary ? foreignOneTimePrice(tz).label : `${CALC_UNLOCK_PRICE} ₽`;
}
export const ONE_TIME_PRICE_LABEL = oneTimePriceLabel();

// При Робокассе: фразы о разовой покупке в текстах программ
// (src/seo/*.json) → только ПРО. Порядок важен: длинные фразы раньше.
export const NO_ONE_TIME_PHRASES = [
  ['открывается разово за 199 ₽ или входит в ПРО-подписку', 'входит в ПРО-подписку'],
  ['Полный разбор стоит 199 ₽ разово. Также он входит в ПРО-подписку', 'Полный разбор входит в ПРО-подписку'],
  ['Разово за 199 ₽ открывается вся пара целиком', 'В ПРО открывается вся пара целиком'],
  ['разово за 199 ₽ или в ПРО', 'в ПРО'],
  ['за 199 ₽ или в ПРО', 'в ПРО'],
];

/**
 * Текст программы из src/seo/*.json для страницы: название и адрес своего
 * сайта (siteText) и разовая цена в валюте посетителя — «за 199 ₽» у
 * иностранца на ру становится «за 2,49 $» / «за 1 190 ₸», как на кнопке
 * «Открыть полностью». На рф цена остаётся 199 ₽. При Робокассе
 * (offered = false) разовую покупку не обещаем: остаётся только ПРО.
 */
export function programText(value, priceLabel = ONE_TIME_PRICE_LABEL, host = undefined,
  offered = oneTimeOffered()) {
  const named = siteText(value, host || SITE_HOST);
  const rub = `${CALC_UNLOCK_PRICE} ₽`;
  if (offered && priceLabel === rub) return named;
  const fix = (text) => (offered
    ? text.split(rub).join(priceLabel)
    : NO_ONE_TIME_PHRASES.reduce((t, [from, to]) => t.split(from).join(to), text));
  const swap = (v) => {
    if (typeof v === 'string') return fix(v);
    if (Array.isArray(v)) return v.map(swap);
    if (v && typeof v === 'object') {
      const out = {};
      Object.entries(v).forEach(([k, val]) => { out[k] = swap(val); });
      return out;
    }
    return v;
  };
  return swap(named);
}

/** Хвост заголовков замков: «за 199 ₽ или в ПРО», при Робокассе — «в ПРО». */
export function priceOrPro(app, isRobokassa = rubIsRobokassa(), stripePrimary = STRIPE_PRIMARY) {
  return oneTimeSaleShown(app, isRobokassa, stripePrimary)
    ? `за ${oneTimePriceLabel(stripePrimary)} или в ПРО` : 'в ПРО';
}

function authHeaders() {
  let token = null;
  try { token = localStorage.getItem('auth_token'); } catch (_e) { token = null; }
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Открыт ли расчёт разово. Гостю и при сбое — нет. */
export async function checkCalcUnlock(app, payload) {
  try {
    const res = await fetch(`${API_URL}/api/calculator/unlocks/check`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ app, payload }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    return !!data.unlocked;
  } catch (_e) {
    return false;
  }
}

/**
 * Купить расчёт. Возвращает { unlocked: true } — уже открыт (пересчитать),
 * { redirected: true } — ушли в кассу, { error } — показать человеку.
 */
export function buyCalcUnlock(app, payload, opts = {}) {
  // Одна покупка за раз: повторный тап по кнопке или замку получает тот же
  // результат, второй платёж не заводится (lib/checkoutGuard.js).
  return singleCheckout(() => buyCalcUnlockNow(app, payload, opts), (res) => !!(res && res.redirected));
}

async function buyCalcUnlockNow(app, payload, { returnPath, viaStripe = false } = {}) {
  const cfg = CALC_UNLOCK_APPS[app];
  if (!cfg) return { error: 'Неизвестная программа' };
  ymGoal(`${app}_buy_click`, { plan: cfg.plan });
  let saved;
  try {
    const res = await fetch(`${API_URL}/api/calculator/unlocks`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ app, payload }),
    });
    saved = await res.json().catch(() => ({}));
    if (!res.ok) return { error: saved.detail || 'Не удалось подготовить оплату' };
  } catch (_e) {
    return { error: 'Нет связи с сервером' };
  }
  if (saved.unlocked) return { unlocked: true };
  try {
    localStorage.setItem(CALC_UNLOCK_RETURN_KEY, saved.id);
    if (returnPath) localStorage.setItem(CALC_UNLOCK_RETURN_PATH_KEY, returnPath);
    else localStorage.removeItem(CALC_UNLOCK_RETURN_PATH_KEY);
  } catch (_e) { /* no-op */ }
  const ymClientId = await ymClientIdAsync().catch(() => null);
  // Иностранцу на ру — Stripe; на рф Stripe — по ссылке «Оплатить
  // иностранной картой» (viaStripe); всем остальным основная касса.
  const result = (STRIPE_PRIMARY || viaStripe)
    ? await createOneTimePaymentNow({
      plan: cfg.plan, ymClientId, extra: { calc_unlock_id: saved.id }, stripePrimary: true,
    })
    : await createPrimaryPaymentNow({ plan: cfg.plan, ymClientId, extra: { calc_unlock_id: saved.id } });
  if (result.ok && result.redirectUrl) {
    window.location.href = result.redirectUrl;
    return { redirected: true };
  }
  if (result.status === 409) return { unlocked: true };
  return { error: result.error || 'Не удалось создать платёж' };
}

/** id расчёта из адреса (?unlock=…) — возврат после оплаты. */
export function unlockIdFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get(CALC_UNLOCK_PARAM);
  } catch (_e) {
    return null;
  }
}

/** Сохранённый расчёт по id: { app, payload, unlocked } или null. */
export async function loadCalcUnlock(id) {
  if (!id) return null;
  try {
    const res = await fetch(`${API_URL}/api/calculator/unlocks/${encodeURIComponent(id)}`, {
      headers: authHeaders(),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (_e) {
    return null;
  }
}
